/**
 * Accès direct au bucket Supabase Storage `synoptic-projects` via fetch REST.
 *
 * ⚠️ N'utilise volontairement PAS le client supabase-js : la sauvegarde des
 * données projet est critique et ne doit pas dépendre d'un client qui peut
 * devenir zombie après une suspension d'onglet (bug #36046).
 *
 * Le blob `data` (JSON gzippé) transite directement entre le navigateur et
 * Supabase Storage → contourne la limite de 6 Mo des routes API Netlify.
 */

const SUPABASE_URL = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
const SUPABASE_ANON_KEY = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY || ''

export const SYNOPTIC_BUCKET = 'synoptic-projects'

/** Chemin conventionnel du blob data d'un projet dans le bucket */
export function synopticDataPath(userId: string, projectId: string): string {
  return `${userId}/${projectId}.json.gz`
}

export class SynopticStorageError extends Error {
  constructor(
    message: string,
    readonly status: number
  ) {
    super(message)
    this.name = 'SynopticStorageError'
  }
}

const GZIP_MAGIC = 0x1f8b

async function gzipText(text: string): Promise<Blob> {
  const stream = new Blob([text]).stream().pipeThrough(new CompressionStream('gzip'))
  return new Response(stream).blob()
}

/** Décode un blob de données projet (gzip ou JSON brut) en objet JS */
export async function decodeSynopticProjectData(blob: Blob): Promise<unknown> {
  const buffer = await blob.arrayBuffer()
  const view = new DataView(buffer)
  const isGzip = buffer.byteLength >= 2 && view.getUint16(0, false) === GZIP_MAGIC
  const text = isGzip
    ? await new Response(
        new Blob([buffer]).stream().pipeThrough(new DecompressionStream('gzip'))
      ).text()
    : new TextDecoder().decode(buffer)
  return JSON.parse(text)
}

/**
 * Upload (upsert) le blob data d'un projet dans Storage.
 * Retourne le chemin de l'objet (à persister dans `data_path`).
 */
export async function uploadSynopticProjectData(options: {
  userId: string
  projectId: string
  accessToken: string
  data: unknown
  timeoutMs?: number
}): Promise<string> {
  const { userId, projectId, accessToken, data, timeoutMs = 60000 } = options
  const path = synopticDataPath(userId, projectId)
  const body = await gzipText(JSON.stringify(data))

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(
      `${SUPABASE_URL}/storage/v1/object/${SYNOPTIC_BUCKET}/${path}`,
      {
        method: 'POST',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`,
          'x-upsert': 'true',
          'Content-Type': 'application/gzip'
        },
        body,
        signal: controller.signal
      }
    )
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new SynopticStorageError(`UPLOAD_${response.status}: ${text}`, response.status)
    }
    return path
  } finally {
    clearTimeout(timer)
  }
}

/** Télécharge et décode le blob data d'un projet (propriétaire ou admin) */
export async function downloadSynopticProjectData(options: {
  dataPath: string
  accessToken: string
  timeoutMs?: number
}): Promise<unknown> {
  const { dataPath, accessToken, timeoutMs = 60000 } = options
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(
      `${SUPABASE_URL}/storage/v1/object/authenticated/${SYNOPTIC_BUCKET}/${dataPath}`,
      {
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`
        },
        signal: controller.signal
      }
    )
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new SynopticStorageError(`DOWNLOAD_${response.status}: ${text}`, response.status)
    }
    return await decodeSynopticProjectData(await response.blob())
  } finally {
    clearTimeout(timer)
  }
}

/** Télécharge et décode un blob data via une URL signée (partage public) */
export async function fetchSharedProjectData(signedUrl: string, timeoutMs = 60000): Promise<unknown> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(signedUrl, { signal: controller.signal })
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new SynopticStorageError(`SHARED_${response.status}: ${text}`, response.status)
    }
    return await decodeSynopticProjectData(await response.blob())
  } finally {
    clearTimeout(timer)
  }
}

/** Supprime le blob data d'un projet (best-effort à l'appelant) */
export async function deleteSynopticProjectData(options: {
  dataPath: string
  accessToken: string
  timeoutMs?: number
}): Promise<void> {
  const { dataPath, accessToken, timeoutMs = 15000 } = options
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  try {
    const response = await fetch(
      `${SUPABASE_URL}/storage/v1/object/${SYNOPTIC_BUCKET}/${dataPath}`,
      {
        method: 'DELETE',
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${accessToken}`
        },
        signal: controller.signal
      }
    )
    if (!response.ok) {
      const text = await response.text().catch(() => '')
      throw new SynopticStorageError(`DELETE_${response.status}: ${text}`, response.status)
    }
  } finally {
    clearTimeout(timer)
  }
}
